import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../hooks';
import proofkitFilemakerService from '../../services/proofkitFilemakerService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui';
import { ZCampaigns } from '../../schema/Campaigns';
import formatters from '../../styles/tokens/formatters';

const CAMPAIGNS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGNS || "campaigns";
const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";
const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";
const CAMPAIGN_RECIPIENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGN_RECIPIENTS || "campaign_recipients";
const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";
const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function CampaignsForm() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [segmentMap, setSegmentMap] = useState({});

  const [formData, setFormData] = useState({
    nombre: "",
    template_id: "",
    estado: "borrador",
    fecha_envio: "",
  });

  const [contactsQuery, setContactsQuery] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(() => new Set());

  const templatesOptions = useMemo(() => {
    return (Array.isArray(templates) ? templates : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const id = fieldData?.ClavePrincipal || r?.recordId || "";
      const nombre = fieldData?.nombre || id;
      return { id, nombre };
    });
  }, [templates]);

  const contactsRows = useMemo(() => {
    const q = String(contactsQuery || "").trim().toLowerCase();
    const base = (Array.isArray(contacts) ? contacts : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const id = fieldData?.ClavePrincipal || r?.recordId || "";
      const nombre = fieldData?.nombre || "";
      const apellido = fieldData?.apellido || "";
      const email = fieldData?.email || "";
      const fullName = `${nombre} ${apellido}`.trim();
      const segments = segmentMap[id] || [];
      return { id, nombre, apellido, email, fullName, segments };
    });

    if (!q) return base;

    return base.filter((c) => {
      const h = `${c.fullName} ${c.email} ${(c.segments || []).join(" ")}`.toLowerCase();
      return h.includes(q);
    });
  }, [contacts, contactsQuery, segmentMap]);

  const selectedCount = useMemo(() => selectedContactIds.size, [selectedContactIds]);

  const toggleContact = (id, checked) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllFiltered = (checked) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      contactsRows.forEach((c) => {
        if (checked) next.add(c.id);
        else next.delete(c.id);
      });
      return next;
    });
  };

  const allFilteredSelected = useMemo(() => {
    if (!contactsRows.length) return false;
    return contactsRows.every((c) => selectedContactIds.has(c.id));
  }, [contactsRows, selectedContactIds]);

  const someFilteredSelected = useMemo(() => {
    if (!contactsRows.length) return false;
    return contactsRows.some((c) => selectedContactIds.has(c.id));
  }, [contactsRows, selectedContactIds]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [templatesRes, contactsRes, segmentsRes, contactSegmentsRes] = await Promise.all([
          proofkitFilemakerService.getAllRecords(TEMPLATES_LAYOUT),
          proofkitFilemakerService.getAllRecords(CONTACTS_LAYOUT),
          proofkitFilemakerService.getAllRecords(SEGMENTS_LAYOUT),
          proofkitFilemakerService.getAllRecords(CONTACT_SEGMENTS_LAYOUT),
        ]);

        setTemplates(Array.isArray(templatesRes?.data) ? templatesRes.data : []);
        setContacts(Array.isArray(contactsRes?.data) ? contactsRes.data : []);

        const segmentsRaw = Array.isArray(segmentsRes?.data) ? segmentsRes.data : [];
        const contactSegmentsRaw = Array.isArray(contactSegmentsRes?.data) ? contactSegmentsRes.data : [];

        const segmentsById = {};
        segmentsRaw.forEach((row) => {
          const fd = row?.fieldData || {};
          const segId = fd?.ClavePrincipal || row?.recordId;
          const nombre = fd?.nombre || "";
          if (segId) segmentsById[String(segId)] = nombre;
        });

        const map = {};
        contactSegmentsRaw.forEach((row) => {
          const fd = row?.fieldData || {};
          const contactId = fd?.contact_id;
          const segmentId = fd?.segment_id;
          if (!contactId || !segmentId) return;
          const segName = segmentsById[String(segmentId)] || "";
          if (!segName) return;
          const key = String(contactId);
          map[key] = map[key] || [];
          if (!map[key].includes(segName)) {
            map[key].push(segName);
          }
        });

        setSegmentMap(map);
      } catch (err) {
        toast({
          title: "Error",
          description: "No se pudieron cargar plantillas/contacts/segmentos desde FileMaker",
          variant: "destructive",
        });
        setTemplates([]);
        setContacts([]);
        setSegmentMap({});
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const templateId = String(formData?.template_id || "").trim();

    if (!nombre) {
      return toast({ title: "Falta nombre", description: "Debes indicar el nombre de la campaña", variant: "destructive" });
    }

    if (!templateId) {
      return toast({ title: "Falta plantilla", description: "Debes seleccionar una plantilla", variant: "destructive" });
    }

    if (!selectedContactIds.size) {
      return toast({ title: "Faltan contactos", description: "Debes seleccionar al menos 1 contacto", variant: "destructive" });
    }

    try {
      setSaving(true);

      const campaignFieldData = ZCampaigns
        .pick({ nombre: true, template_id: true, estado: true, activo: true, fecha_envio: true })
        .partial()
        .parse({
          nombre,
          template_id: templateId,
          estado: formData?.estado || "borrador",
          activo: 1,
        });

      const fechaEnvioRaw = String(formData?.fecha_envio || "").trim();
      if (fechaEnvioRaw) {
        const fechaEnvio = formatters.toFileMakerDateTimeUS24(fechaEnvioRaw);
        if (fechaEnvio) campaignFieldData.fecha_envio = fechaEnvio;
      }

      const createRes = await proofkitFilemakerService.createRecord(CAMPAIGNS_LAYOUT, campaignFieldData);
      const createdRecordId = createRes?.recordId;

      if (!createdRecordId) {
        throw new Error("No se pudo obtener el ID de la campaña creada");
      }

      const createdRecord = await proofkitFilemakerService.getRecord(CAMPAIGNS_LAYOUT, createdRecordId);
      const campaignKey = createdRecord?.data?.fieldData?.ClavePrincipal || createdRecordId;

      const recipients = Array.from(selectedContactIds);
      const contactKeys = await Promise.all(
        recipients.map(async (cid) => {
          try {
            const res = await proofkitFilemakerService.getRecord(CONTACTS_LAYOUT, cid);
            const fd = res?.data?.fieldData || {};
            return fd?.ClavePrincipal || cid;
          } catch (_) {
            const fr = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, { query: [{ ClavePrincipal: cid }], limit: 1 });
            const fd = fr?.data?.[0]?.fieldData || {};
            return fd?.ClavePrincipal || cid;
          }
        })
      );
      await Promise.all(
        contactKeys.map((contactKey) =>
          proofkitFilemakerService.createRecord(CAMPAIGN_RECIPIENTS_LAYOUT, {
            campaign_id: campaignKey,
            contact_id: contactKey,
            estado: "pendiente",
          })
        )
      );

      toast({ title: "Campaña creada", description: "La campaña quedó creada con su plantilla y contactos" });
      navigate("/campanas");
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message ? `No se pudo crear la campaña: ${err.message}` : "No se pudo crear la campaña",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Nueva Campaña</h1>
        <Button variant="outline" onClick={() => navigate("/campanas")}>
          Volver
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Crear campaña</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando plantillas y contactos...</div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input id="nombre" name="nombre" value={formData.nombre} onChange={handleInputChange} />
                </div>

                <div className="space-y-2">
                  <Label>Plantilla</Label>
                  <Select value={formData.template_id} onValueChange={(v) => handleSelectChange("template_id", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {templatesOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={formData.estado} onValueChange={(v) => handleSelectChange("estado", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="borrador">Borrador</SelectItem>
                      <SelectItem value="programada">Programada</SelectItem>
                      <SelectItem value="enviada">Enviada</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fecha_envio">Fecha envío</Label>
                  <Input
                    id="fecha_envio"
                    name="fecha_envio"
                    type="datetime-local"
                    value={formData.fecha_envio}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Contactos ({selectedCount} seleccionados)</Label>
                  <div className="w-full max-w-sm">
                    <Input
                      placeholder="Buscar por nombre o email..."
                      value={contactsQuery}
                      onChange={(e) => setContactsQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={allFilteredSelected || (someFilteredSelected && "indeterminate")}
                              onCheckedChange={(v) => toggleAllFiltered(!!v)}
                              aria-label="Seleccionar todos"
                            />
                          </div>
                        </TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Segmento(s)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contactsRows.length ? (
                        contactsRows.map((c) => (
                          <TableRow key={c.id} data-state={selectedContactIds.has(c.id) ? "selected" : undefined}>
                            <TableCell>
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={selectedContactIds.has(c.id)}
                                  onCheckedChange={(v) => toggleContact(c.id, !!v)}
                                  aria-label="Seleccionar contacto"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{c.fullName || "Sin nombre"}</TableCell>
                            <TableCell>{c.email || "-"}</TableCell>
                            <TableCell>{(c.segments || []).join(", ") || "-"}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="h-24 text-center">
                            No hay contactos.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate("/campanas")} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? "Creando..." : "Crear Campaña"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
